import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    PencilSquareIcon,
    ArrowDownTrayIcon,
    XMarkIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    AcademicCapIcon
} from '@heroicons/react/24/outline';

const BulkGrading = () => {
    const { assignmentId } = useParams();
    const navigate = useNavigate();
    const [assignment, setAssignment] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [gradedCount, setGradedCount] = useState(0);
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState({
        competency_level: '',
        grade: '',
        teacher_comment: '',
        evidence: ''
    });

    useEffect(() => {
        fetchAssignmentData();
    }, [assignmentId, fetchAssignmentData]);

    useEffect(() => {
        // Keyboard shortcuts
        const handleKeyPress = (e) => {
            if (e.target.tagName === 'TEXTAREA' || e.target.tagName === 'INPUT') return;

            switch (e.key) {
                case '1': handleLevelSelect('EE'); break;
                case '2': handleLevelSelect('ME'); break;
                case '3': handleLevelSelect('AE'); break;
                case '4': handleLevelSelect('BE'); break;
                case 'Enter':
                    if ((e.ctrlKey || e.metaKey) && formData.competency_level) {
                        e.preventDefault();
                        handleSaveAndNext();
                    }
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [formData, currentIndex, handleLevelSelect, handleSaveAndNext]);

    const fetchAssignmentData = useCallback(async () => {
        try {
            setLoading(true);
            const [assignmentRes, submissionsRes] = await Promise.all([
                axios.get(`/api/courses/assignments/${assignmentId}/`),
                axios.get(`/api/courses/assignments/${assignmentId}/submissions/`)
            ]);

            setAssignment(assignmentRes.data);
            setSubmissions(submissionsRes.data);

            // Fetch Assessment Templates for this Learning Area
            if (assignmentRes.data.learning_area || assignmentRes.data.course) {
                const laId = assignmentRes.data.learning_area || assignmentRes.data.course;
                const templatesRes = await axios.get(`/api/cbc/templates/by_learning_area/?learning_area_id=${laId}`);
                setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
            }

            // Check for existing grading session
            const sessionsRes = await axios.get(`/api/cbc/bulk-grading/?assignment=${assignmentId}`);
            if (sessionsRes.data.length > 0) {
                setSession(sessionsRes.data[0]);
                setGradedCount(sessionsRes.data[0].graded_count);
            } else {
                // Create new session
                const newSession = await axios.post('/api/cbc/bulk-grading/', {
                    assignment: assignmentId,
                    total_submissions: submissionsRes.data.length
                });
                setSession(newSession.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [assignmentId]);

    const getSuggestedScore = useCallback((levelCode) => {
        const total = assignment?.total_marks || 20;
        switch (levelCode) {
            case 'EE': return Math.round(total * 0.9);
            case 'ME': return Math.round(total * 0.7);
            case 'AE': return Math.round(total * 0.5);
            case 'BE': return Math.round(total * 0.3);
            default: return '';
        }
    }, [assignment]);

    const handleLevelSelect = useCallback((code) => {
        const suggested = getSuggestedScore(code);
        setFormData(prev => ({
            ...prev,
            competency_level: code,
            grade: suggested,
            evidence: prev.evidence || `Demonstrated ${code} in ${assignment.title}`
        }));
    }, [assignment, getSuggestedScore]);

    const handleSaveAndNext = useCallback(async () => {
        if (!formData.competency_level) {
            alert('Please select a competency level');
            return;
        }

        if (!formData.evidence.trim()) {
            alert('Please provide evidence description');
            return;
        }

        try {
            const currentSubmission = submissions[currentIndex];

            // Handle multiple outcomes: Combine primary outcome and tested outcomes
            const outcomeMap = new Map();
            if (assignment.learning_outcome) {
                outcomeMap.set(assignment.learning_outcome, { id: assignment.learning_outcome });
            }
            if (assignment.tested_outcomes_detail?.length > 0) {
                assignment.tested_outcomes_detail.forEach(o => outcomeMap.set(o.id, o));
            }
            const outcomes = Array.from(outcomeMap.values());

            // 1. Update the Assignment Submission if it exists
            if (currentSubmission.id) {
                await axios.patch(`/api/courses/assignment-submissions/${currentSubmission.id}/`, {
                    grade: formData.grade,
                    competency_level: formData.competency_level,
                    status: 'graded'
                });
            }

            // 2. Save competency assessment for each outcome
            for (const outcome of outcomes) {
                await axios.post('/api/cbc/competency-assessments/', {
                    student: currentSubmission.student.id,
                    learning_outcome: outcome.id,
                    competency_level: formData.competency_level,
                    teacher_comment: formData.teacher_comment,
                    evidence: formData.evidence,
                    assignment_submission: currentSubmission.id
                });
            }

            // Update session progress
            await axios.post(`/api/cbc/bulk-grading/${session.id}/update-progress/`);

            setGradedCount(prev => prev + 1);

            // Move to next or finish
            if (currentIndex < submissions.length - 1) {
                setCurrentIndex(prev => prev + 1);
                resetForm();
            } else {
                alert('All submissions graded! 🎉');
                navigate(-1);
            }
        } catch (error) {
            console.error('Error saving grade:', error);
            alert('Failed to save grade');
        }
    }, [assignment, currentIndex, formData, navigate, session, submissions]);

    const handleSkip = () => {
        if (currentIndex < submissions.length - 1) {
            setCurrentIndex(prev => prev + 1);
            resetForm();
        }
    };

    const resetForm = () => {
        setFormData({
            competency_level: '',
            grade: '',
            teacher_comment: '',
            evidence: ''
        });
    };

    const currentSubmission = submissions[currentIndex];
    const progressPercentage = submissions.length > 0
        ? Math.round((gradedCount / submissions.length) * 100)
        : 0;

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!assignment || submissions.length === 0) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                    <p className="text-yellow-800">No submissions to grade</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 relative">
            {/* Unified Backdrop Overlay */}
            <div className="fixed inset-0 bg-[#18216D]/60 backdrop-blur-md -z-10" />

            {/* Centralized Grading Card */}
            <div className="max-w-4xl mx-auto py-12 px-6">
                <div className="bg-white rounded-[3rem] shadow-2xl border border-white/20 overflow-hidden">

                    {/* Premium Card Header */}
                    <div className="px-10 py-8 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between gap-6">
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 bg-[#18216D] rounded-[1.5rem] flex items-center justify-center text-white font-black text-2xl shadow-xl shadow-indigo-900/20">
                                {currentSubmission.student.first_name[0]}
                            </div>
                            <div className="space-y-1">
                                <h2 className="text-2xl font-black text-[#18216D] tracking-tight">
                                    {currentSubmission.student.first_name} {currentSubmission.student.last_name}
                                </h2>
                                <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                    <span>ID: {currentSubmission.student.student_id || 'N/A'}</span>
                                    <span className="w-1 h-1 bg-slate-300 rounded-full" />
                                    <span>Handed in: {new Date(currentSubmission.submitted_at).toLocaleDateString()}</span>
                                </div>
                            </div>
                        </div>

                        {/* Card Header Actions */}
                        <div className="flex items-center gap-3">
                            {currentSubmission.file_url && (
                                <a
                                    href={currentSubmission.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-4 bg-white border border-slate-100 hover:bg-indigo-50 hover:text-indigo-600 rounded-2xl text-slate-400 transition-all shadow-sm"
                                    title="Download Evidence"
                                >
                                    <ArrowDownTrayIcon className="w-5 h-5" />
                                </a>
                            )}
                            <button
                                onClick={() => navigate(`/teacher/active-marking/${assignment.id}/${currentSubmission.id}`)}
                                className="p-4 bg-white border border-slate-100 hover:bg-emerald-50 hover:text-emerald-600 rounded-2xl text-slate-400 transition-all shadow-sm"
                                title="Launch Marking Canvas"
                            >
                                <PencilSquareIcon className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => navigate(-1)}
                                className="p-4 bg-white border border-slate-100 hover:bg-red-50 hover:text-red-500 rounded-2xl text-slate-300 transition-all shadow-sm"
                                title="Exit Hub"
                            >
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Progress Indicator (Mini) */}
                    <div className="h-2 bg-slate-100 w-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-[#18216D] to-[#3B82F6] transition-all duration-700 ease-out"
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>

                    {/* Card Body - Marking Area */}
                    <div className="p-10 space-y-10">

                        {/* 1. Template Selector */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-slate-50">
                            <div className="space-y-1">
                                <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-400">Assessment Blueprint</h4>
                                <p className="text-xs text-slate-400 italic">Select a template to load descriptive criteria</p>
                            </div>
                            <select
                                value={selectedTemplate?.id || ''}
                                onChange={(e) => {
                                    const t = templates.find(temp => temp.id === parseInt(e.target.value));
                                    setSelectedTemplate(t || null);
                                }}
                                className="min-w-[240px] px-6 py-3 bg-slate-50 border-none rounded-xl font-black text-[#18216D] text-[10px] tracking-widest uppercase appearance-none cursor-pointer hover:bg-slate-100 transition-all shadow-inner"
                            >
                                <option value="">Custom Marking</option>
                                {templates.map(t => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </div>

                        {/* 2. Competency Framework (Standardized) */}
                        <div>
                            <div className="flex items-center justify-between mb-8">
                                <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-400">Competency Level</h4>
                                <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest bg-slate-50 px-2 py-1 rounded">Keys: 1-4</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {[
                                    { value: 'EE', label: 'Exceeding', color: 'amber', key: '1', border: 'border-amber-400/30', bg: 'bg-amber-50', text: 'text-amber-700' },
                                    { value: 'ME', label: 'Meeting', color: 'emerald', key: '2', border: 'border-emerald-400/30', bg: 'bg-emerald-50', text: 'text-emerald-700' },
                                    { value: 'AE', label: 'Approaching', color: 'blue', key: '3', border: 'border-blue-400/30', bg: 'bg-blue-50', text: 'text-blue-700' },
                                    { value: 'BE', label: 'Below', color: 'rose', key: '4', border: 'border-rose-400/30', bg: 'bg-rose-50', text: 'text-rose-700' }
                                ].map(level => (
                                    <button
                                        key={level.value}
                                        type="button"
                                        onClick={() => handleLevelSelect(level.value)}
                                        className={`p-6 rounded-3xl border-2 transition-all text-left relative overflow-hidden group ${formData.competency_level === level.value
                                            ? `${level.border} ${level.bg} shadow-xl shadow-indigo-900/5`
                                            : 'border-slate-100 bg-white hover:border-[#18216D]/20'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className={`text-xl font-black ${level.text}`}>{level.value}</span>
                                            <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest group-hover:text-slate-400">[{level.key}]</span>
                                        </div>
                                        <div className="text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-2">{level.label} Expectations</div>

                                        {/* Template Assistive Text */}
                                        {selectedTemplate && (
                                            <p className="text-[10px] text-slate-500 italic font-medium leading-relaxed mt-2 pt-2 border-t border-slate-100/50 line-clamp-2 group-hover:line-clamp-none transition-all">
                                                {selectedTemplate[`criteria_${level.value.toLowerCase()}`]}
                                            </p>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* 3. Suggestive Score & Form */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pt-6">
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Score (Optional)</label>
                                    <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100 shadow-inner">
                                        <input
                                            type="number"
                                            value={formData.grade}
                                            onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                                            className="bg-transparent font-black text-xl text-[#18216D] outline-none w-20"
                                            placeholder="0"
                                        />
                                        <span className="text-slate-300 font-black">/ {assignment?.total_marks || 20}</span>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Evidence of Competency</label>
                                    <textarea
                                        value={formData.evidence}
                                        onChange={(e) => setFormData({ ...formData, evidence: e.target.value })}
                                        rows="4"
                                        className="w-full p-6 bg-slate-50 border-none rounded-[1.5rem] font-bold text-slate-600 text-sm focus:ring-2 focus:ring-[#18216D]/10 transition-all shadow-inner placeholder:text-slate-200"
                                        placeholder="What did the scholar demonstrate?"
                                    />
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Personalized Feedback</label>
                                    <textarea
                                        value={formData.teacher_comment}
                                        onChange={(e) => setFormData({ ...formData, teacher_comment: e.target.value })}
                                        rows="4"
                                        className="w-full p-6 bg-slate-50 border-none rounded-[1.5rem] font-bold text-slate-600 text-sm focus:ring-2 focus:ring-[#18216D]/10 transition-all shadow-inner placeholder:text-slate-200"
                                        placeholder="Add a comment to the student report..."
                                    />
                                </div>

                                {/* Hub Navigation */}
                                <div className="flex items-center gap-2 pt-4">
                                    <button
                                        onClick={handleSaveAndNext}
                                        disabled={!formData.competency_level || !formData.evidence}
                                        className="flex-1 px-8 py-5 bg-[#18216D] text-white font-black rounded-2xl hover:bg-[#0D164F] shadow-xl shadow-indigo-900/20 active:scale-95 transition-all uppercase tracking-widest text-[11px] disabled:opacity-50 disabled:grayscale disabled:scale-100"
                                    >
                                        Save & Proceed
                                    </button>
                                    <button
                                        onClick={handleSkip}
                                        className="p-5 bg-slate-100 text-slate-400 rounded-2xl hover:bg-slate-200 transition-all"
                                        title="Skip for now"
                                    >
                                        <ChevronRightIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Visual Legend (Mini) */}
                        <div className="flex flex-wrap items-center justify-center gap-6 pt-10 border-t border-slate-50">
                            {[
                                { k: '1', l: 'EE' }, { k: '2', l: 'ME' }, { k: '3', l: 'AE' }, { k: '4', l: 'BE' }, { k: '⌘⏎', l: 'Save' }
                            ].map(x => (
                                <div key={x.l} className="flex items-center gap-2">
                                    <kbd className="px-2 py-1 bg-slate-100 text-slate-400 rounded text-[9px] font-black font-mono shadow-sm">{x.k}</kbd>
                                    <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest">{x.l}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Assignment Info Footer */}
                <div className="mt-8 text-center space-y-2">
                    <p className="text-[11px] font-black text-slate-300 uppercase tracking-[0.3em]">Currently Grading</p>
                    <h3 className="text-lg font-black text-[#18216D]/40 tracking-tight italic">{assignment.title}</h3>
                </div>
            </div>
        </div>
    );
};

export default BulkGrading;
